import { InstrumentT, OrderType, RewardT, RewardType } from '@/common/types'
import useAccountStore from '@/account/accountStore'
import { useFetchBalance } from '@/wallet/walletQueries'
import { FC, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { AlertCircle, Gift, Info } from 'lucide-react'
import { OrderTypeSelector } from '@/trading/OrderTypeSelector'
import Money from '@/common/models/money'
import { Button } from '@/common/ui/button'
import { LabelInput } from '@/common/ui/LabelInput'
import { APP_ROUTES } from '@/common/constants'
import useBundleTrade from './useBundleTrade'
import { Accordion } from '@radix-ui/react-accordion'
import SecurityOrderAccordionItem from './SecurityOrderAccordionItem'
import { useListHoldings } from '@/trading/tradingQueries'
import { Alert } from '@/common/ui/alert'
import { InfoIconTooltip } from '@/common/ui/tooltip'
import { ORDER_FORM_COPY, BUNDLE_COPY } from '@/locales/en'
import { H1 } from '@/common/ui'
import calculateInitialAllocation from './bundleAllocation'
import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { useFetchRewards } from '@/user/userQueries'
import { OrderRewardItem } from '@/trading/orderRewardItem'
import { useOrderSource } from '@/common/context/useOrderSource'
import { ORDER_ENTRY_POINT, ORDER_FROM } from '@/common/context/orderSourceValues'

type Props = {
    instruments: (InstrumentT & { weight: number })[]
    hasHIN: boolean
    onClose?: () => void
    resetForm: () => void
}

export const OrderForm: FC<Props> = ({ onClose, instruments, resetForm }) => {
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const { data: balance } = useFetchBalance(activeAccountId)
    const { data: rewards } = useFetchRewards()
    const { data: holdingsData, isLoading: holdingsLoading } = useListHoldings(activeAccountId)
    const {
        orderItems: lines,
        setOrderItems,
        updateOrderItem: updateLine,
        totals,
        orderType,
        setOrderType,
        totalInvestmentAmount,
        setTotalInvestmentAmount,
        hasFundsError,
        setHasFundsError,
        name,
        bundleId,
        promotions,
        setPromotions,
    } = useBundleTrade()
    const navigate = useNavigate()

    const initRunRef = useRef(false)
    const calculationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const isMarketClosed = !(
        lines?.[0]?.instrument.priceInfo.status === 'ACTIVE' ||
        lines?.[0]?.instrument.priceInfo.status === 'TRADING' ||
        lines?.[0]?.instrument.priceInfo.status === 'OPEN' ||
        lines?.[0]?.instrument.priceInfo.status === 'PRE_OPEN'
    )
    const initialOrderType = isMarketClosed ? 'LIMIT' : 'MARKET_TO_LIMIT'

    const navToWallet = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        onClose?.()
        analytics.trackWithDefaults(ANALYTICS_EVENTS.WALLET.ADD_FUNDS_BUTTON_CLICKED, {
            bundle_id: bundleId,
            source_page: 'bundle',
        })
        navigate(APP_ROUTES.WALLET)
    }

    useEffect(() => {
        // Run once on mount (when modal opens) after holdings have loaded
        // and we have a concrete `sharesCurrentlyHeld` value.
        if (initRunRef.current) return
        if (holdingsLoading) return
        if (lines.length === 0) return
        setOrderType(initialOrderType)
        initRunRef.current = true
    }, [initialOrderType, setOrderType, holdingsLoading, lines])

    const selectPromotions = (rewards?: RewardT[], needed = 0): RewardT[] => {
        if (!rewards || needed <= 0) return []
        const applied: RewardT[] = []
        let remaining = needed
        for (const r of rewards) {
            if (r.promotionType !== RewardType.FREE_TRADE_REBATE) continue
            const qty = r.quantityRemaining ?? 0
            if (qty <= 0) continue
            applied.push(r)
            if (qty >= remaining) break
            remaining -= qty
        }
        return applied
    }

    const selectedPromotions = useMemo(() => selectPromotions(rewards, lines.length), [rewards, lines.length])

    useEffect(() => {
        if (promotions && promotions.length > 0) return
        if (selectedPromotions.length === 0) return

        const isSame =
            promotions &&
            promotions.length === selectedPromotions.length &&
            promotions.every((p, i) => p.promotionId === selectedPromotions[i].promotionId)

        if (!isSame) setPromotions(selectedPromotions)
    }, [selectedPromotions, promotions, setPromotions])

    useEffect(() => {
        const error = Boolean(
            balance?.availableBalance && totals.totalAmount && balance.availableBalance < totals.totalAmount,
        )
        if (error === hasFundsError) return
        setHasFundsError(error)
    }, [balance?.availableBalance, totals.totalAmount, setHasFundsError, hasFundsError])

    useEffect(() => {
        return () => {
            if (calculationTimeoutRef.current) {
                clearTimeout(calculationTimeoutRef.current)
            }
        }
    }, [])

    if (lines.length === 0 || !orderType) return null

    const errorCount =
        lines.filter((line) => line.hasAmountError || line.hasLimitPriceError).length + (hasFundsError ? 1 : 0)

    const hasAmountError = lines.some((line) => line.hasAmountError)

    const handlerOrderTypeChange = (type: OrderType) => {
        setOrderType(type)
        if (type === 'MARKET_TO_LIMIT') {
            let sum = 0
            lines.forEach((line) => {
                if (line.hasLimitPriceError) {
                    const relevantPrice =
                        line.instrument.priceInfo.lastPrice ?? line.instrument.priceInfo.closingPrice ?? 0
                    const amount = Money.of((line.units ?? 0) * relevantPrice).valueWithPrecision(2)
                    updateLine(line.securityCode, { relevantPrice, amount, hasLimitPriceError: false })
                    sum += amount
                } else {
                    sum += line.amount ?? 0
                }
            })
            setTotalInvestmentAmount(sum)
        }
    }

    const calculateUserInvestmentAmount = (userTotalAmount: number) => {
        if (calculationTimeoutRef.current) {
            clearTimeout(calculationTimeoutRef.current)
        }

        calculationTimeoutRef.current = setTimeout(() => {
            // Calculate the scaling factor based on current vs new total
            const currentTotal = lines.reduce((sum, line) => sum + line.amount, 0)
            if (Math.abs(currentTotal - userTotalAmount) < 0.01) {
                return
            }

            // If current total is zero, recalculate from original weights
            if (currentTotal === 0) {
                const allocations = calculateInitialAllocation(instruments, userTotalAmount)
                const orderItems = allocations.map((a) => {
                    const instrument = instruments.find((i) => i.securityCode === a.securityCode)!
                    return {
                        requestId: crypto.randomUUID(),
                        securityCode: a.securityCode,
                        instrument,
                        amount: a.amount,
                        units: a.units,
                        relevantPrice: a.relevantPrice,
                        weight: userTotalAmount > 0 ? a.amount / userTotalAmount : 0,
                    }
                })

                analytics.trackWithDefaults(ANALYTICS_EVENTS.TRADING.BUNDLE_INVESTMENT_AMOUNT_CHANGED, {
                    bundle_id: bundleId,
                    amount: userTotalAmount,
                    order_items: orderItems.map((item) => ({
                        security_code: item.securityCode,
                        amount: item.amount,
                        units: item.units,
                        weight: item.weight,
                    })),
                })
                setTotalInvestmentAmount(userTotalAmount)
                setOrderItems(orderItems)
                return
            }

            const scaleFactor = userTotalAmount / currentTotal
            // Scale each line proportionally to preserve manual adjustments
            const scaledLines = lines.map((line) => {
                const scaledAmount = line.amount * scaleFactor
                const price = line.relevantPrice
                const newUnits = price > 0 ? Math.floor(scaledAmount / price) : 0
                const actualAmount = new Money(newUnits * price).valueWithPrecision(2)

                return {
                    ...line,
                    amount: actualAmount,
                    units: newUnits,
                }
            })

            analytics.trackWithDefaults(ANALYTICS_EVENTS.TRADING.BUNDLE_INVESTMENT_AMOUNT_CHANGED, {
                bundle_id: bundleId,
                amount: userTotalAmount,
                order_items: scaledLines.map((item) => ({
                    security_code: item.securityCode,
                    amount: item.amount,
                    units: item.units,
                    weight: item.weight,
                })),
            })
            setTotalInvestmentAmount(userTotalAmount)
            setOrderItems(scaledLines)
        }, 1000)
    }

    const onTooltipAmountOpen = (open?: boolean) => {
        if (open) {
            analytics.track(ANALYTICS_EVENTS.TRADING.BUNDLE_TOOLTIP_OPENED, {
                bundle_id: bundleId,
                tooltip: 'amount',
            })
        }
    }

    const onTooltipBrokerageOpen = (open?: boolean) => {
        if (open) {
            analytics.track(ANALYTICS_EVENTS.TRADING.BUNDLE_TOOLTIP_OPENED, {
                bundle_id: bundleId,
                tooltip: 'brokerage',
            })
        }
    }

    const onTooltipOrderOpen = (open?: boolean) => {
        if (open) {
            analytics.track(ANALYTICS_EVENTS.TRADING.BUNDLE_TOOLTIP_OPENED, {
                bundle_id: bundleId,
                tooltip: 'order',
            })
        }
    }

    return (
        <div className="w-full gap-6">
            <H1 className="self-stretch w-full text-3xl justify-start md:text-center text-black font-normal font-['Serious Sans'] leading-9 mb-4">
                {BUNDLE_COPY.buyBundle}
            </H1>
            <ul className="w-full inline-flex flex-col justify-start items-start gap-4">
                <li className="self-stretch inline-flex justify-between items-center border-b border-border-divider pb-4">
                    <div className="justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                        {ORDER_FORM_COPY.bundle}
                    </div>
                    <div className="justify-start text-content-primary text-base font-medium font-['Inter'] leading-6">
                        {name}
                    </div>
                </li>
                <li className="w-full border-b border-border-divider pb-4">
                    <OrderTypeSelector
                        labelClassName="text-content-secondary justify-center"
                        inputClassName="max-w-[100px] justify-end h-9 !text-right px-2"
                        orderType={orderType}
                        setOrderType={handlerOrderTypeChange}
                        defaultOrderType={initialOrderType}
                        isMarketClosed={isMarketClosed}
                        onTooltipOpen={onTooltipOrderOpen}
                    />
                </li>
                <li className="w-full flex flex-col justify-between border-b border-border-divider pb-4">
                    <div className="w-full self-stretch inline-flex justify-between">
                        <div className="flex text-sm text-content-secondary  items-center gap-2">
                            <span>{ORDER_FORM_COPY.availableFunds}:</span>
                        </div>
                        <div className="flex gap-2 text-sm items-center">
                            <span>{Money.of(balance?.availableBalance).toString()}</span>
                            <Button size="sm" variant="tertiary" className="text-xs border-0" onClick={navToWallet}>
                                {ORDER_FORM_COPY.addFunds}
                            </Button>
                        </div>
                    </div>
                    {hasFundsError ? (
                        <p className="text-xs self-start text-sentiment-bad">
                            {ORDER_FORM_COPY.warnings.insufficientFunds}
                        </p>
                    ) : null}
                </li>

                <li className="w-full flex flex-col justify-between border-b border-border-divider pb-4">
                    <LabelInput
                        type="number"
                        className="!pl-0 pr-2 text-right text-sm py-1 items-end h-9"
                        isVertical={false}
                        value={totalInvestmentAmount}
                        prefixIcon={<div className="font-light text-[#6B7280]">$</div>}
                        iconClassName="max-w-[100px]"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value
                            const totalPrice = Money.of(parseFloat(value) || 0).valueWithPrecision(2)
                            calculateUserInvestmentAmount(totalPrice)
                            setTotalInvestmentAmount(totalPrice)
                        }}
                        label={
                            <div className="self-stretch text-content-secondary text-sm font-normal font-['Inter'] inline-flex flex-col justify-center items-start">
                                <div className="self-stretch justify-start leading-5">
                                    {ORDER_FORM_COPY.howMuchToInvest}
                                </div>
                                <div className="self-stretch text-xs justify-start leading-5">
                                    ({ORDER_FORM_COPY.includingBrokerage})
                                </div>
                            </div>
                        }
                        placeholder="0"
                        step=".01"
                        id="investment-amount"
                    />
                    {hasAmountError ? (
                        <p className="text-xs self-start text-sentiment-bad">
                            {ORDER_FORM_COPY.warnings.firstTrade}
                            <a className="text-inherit underline cursor-pointer" onClick={resetForm}>
                                {ORDER_FORM_COPY.setMinimum}
                            </a>
                        </p>
                    ) : null}
                </li>
            </ul>

            <div className="inline-flex w-full flex-col justify-start items-start gap-4 pt-4">
                <div className="text-center justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                    {ORDER_FORM_COPY.breakdown}
                </div>
                <Alert
                    variant={'default'}
                    className="self-stretch p-4 rounded-2xl outline outline-1 outline-offset-[-1px] outline-border-outline inline-flex justify-start items-center gap-2.5 overflow-hidden"
                >
                    <div className="self-stretch inline-flex justify-start items-start gap-4">
                        <div className="w-4 h-4">
                            <Info size={16} className="text-content-primary" />
                        </div>
                        <div className="self-stretch justify-center text-content-tertiary text-sm font-normal font-['Inter'] leading-5">
                            {ORDER_FORM_COPY.bundleAdjustmentInfo}
                        </div>
                    </div>
                </Alert>
                <Accordion
                    type="single"
                    collapsible
                    className="self-stretch w-full flex flex-col justify-start items-start gap-4"
                >
                    {lines.map((line) => (
                        <SecurityOrderAccordionItem
                            key={line.securityCode}
                            line={line}
                            totals={totals}
                            currentHolding={holdingsData?.holdings.find((h) => h.securityCode === line.securityCode)}
                        />
                    ))}
                </Accordion>

                <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider"></div>
                <div className="self-stretch inline-flex justify-between items-center">
                    <div className="inline-flex gap-2 justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                        {ORDER_FORM_COPY.amount}
                        <InfoIconTooltip onOpenChange={onTooltipAmountOpen}>
                            <p className="mb-2 font-bold">{ORDER_FORM_COPY.estimatedAmount}</p>
                            <p className="mb-2">{ORDER_FORM_COPY.infoToolTip.amount}</p>
                        </InfoIconTooltip>
                    </div>
                    <div className="text-center justify-start text-black text-sm font-normal font-['Inter'] leading-5">
                        ${totals.totalAmount.toFixed(2)}
                    </div>
                </div>
                <div className="self-stretch inline-flex justify-between items-start">
                    <div className="w-40 flex justify-start items-center gap-2">
                        <div className="inline-flex gap-2 justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                            {ORDER_FORM_COPY.brokerage}
                            <InfoIconTooltip onOpenChange={onTooltipBrokerageOpen}>
                                <p className="mb-2 font-bold">{ORDER_FORM_COPY.brokerage}</p>
                                <p className="mb-2">{ORDER_FORM_COPY.infoToolTip.brokerage}</p>
                            </InfoIconTooltip>
                        </div>
                    </div>
                    <div className="inline-flex flex-col justify-start items-end">
                        <div className="text-center justify-start text-black text-sm font-normal font-['Inter'] leading-5">
                            ${totals.brokerage.toFixed(2)}
                        </div>
                        <div className="text-center justify-start text-content-secondary text-xs font-normal font-['Inter'] leading-4">
                            {ORDER_FORM_COPY.brokerageAmount}
                        </div>
                    </div>
                </div>

                {promotions && promotions.length > 0 && (
                    <OrderRewardItem
                        icon={<Gift size={20} />}
                        title={ORDER_FORM_COPY.freeTradeItem.title}
                        description={ORDER_FORM_COPY.freeTradeItem.description}
                    />
                )}
                <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider"></div>
                <div className="self-stretch inline-flex justify-between items-center">
                    <div className="text-center justify-start text-content-secondary text-base font-medium font-['Inter'] leading-6">
                        {ORDER_FORM_COPY.total}
                    </div>
                    <div className="text-center justify-start text-black text-base font-medium font-['Inter'] leading-6">
                        {Money.of(totals.total).toString()}
                    </div>
                </div>

                {/* Error Alert if any line has error or funds error */}
                {(lines.some((line) => line.hasAmountError || line.hasLimitPriceError) || hasFundsError) && (
                    <Alert
                        variant={'destructive'}
                        className="self-stretch p-4 rounded-2xl outline outline-1 outline-offset-[-1px] inline-flex justify-start items-center gap-2.5 overflow-hidden"
                    >
                        <div className="flex-1 flex justify-start items-start gap-4">
                            <AlertCircle height={16} width={16} className="text-sentiment-bad" />
                            <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                                <div className="self-stretch justify-center text-sentiment-bad text-sm font-medium font-['Inter'] leading-5">
                                    {errorCount}
                                    {ORDER_FORM_COPY.warnings.numberErrors}
                                </div>
                            </div>
                        </div>
                    </Alert>
                )}
            </div>
        </div>
    )
}

type ButtonProps = {
    bundleId: string
    resetOnClick: () => void
    resetDisabled?: boolean
    reviewDisabled?: boolean
    reviewOnClick: () => void
}

export const OrderFormButtons: React.FC<ButtonProps> = ({
    bundleId,
    resetOnClick,
    reviewOnClick,
    resetDisabled = false,
    reviewDisabled = false,
}) => {
    const { orderSource } = useOrderSource()
    const onResetClick = () => {
        analytics.trackWithDefaults(ANALYTICS_EVENTS.TRADING.BUNDLE_TRADE_RESET, {
            bundle_id: bundleId,
        })
        resetOnClick()
    }

    const onReviewClick = () => {
        analytics.trackWithDefaults(ANALYTICS_EVENTS.TRADING.BUNDLE_TRADE_REVIEWED, {
            bundle_id: bundleId,
            order_from: orderSource.orderFrom ?? ORDER_FROM.BUNDLES,
            order_entry_point: orderSource.orderEntryPoint ?? ORDER_ENTRY_POINT.BUNDLE_CHECKOUT,
        })
        reviewOnClick()
    }

    return (
        <div className="w-full self-stretch inline-flex justify-between items-start gap-6">
            <Button
                variant={'outline'}
                disabled={resetDisabled}
                onClick={onResetClick}
                className="sm:h-16 min-w-[100px] inline-flex justify-center text-black text-sm items-center gap-2.5 font-medium font-['Inter'] leading-5"
            >
                {ORDER_FORM_COPY.reset}
            </Button>
            <Button
                variant={'secondary'}
                disabled={reviewDisabled}
                onClick={onReviewClick}
                className="w-full sm:h-16 sm:w-auto inline-flex justify-center text-black text-sm items-center gap-2.5 font-medium font-['Inter'] leading-5"
            >
                {ORDER_FORM_COPY.review}
            </Button>
        </div>
    )
}
